import { all, call, put, select, takeEvery } from "redux-saga/effects";

import { heapTrack } from "lib/analytics";
import { APPLICATION_STATE } from "lib/constants";
import { rollbarXHRError } from "lib/rollbar";
import { patch, post } from "redux/fetch";
import { hideModal, uiSetSubmitting, uiSetAsyncError } from "redux/ws/actions";

import {
    applicationPatch,
    applicationPatchStart,
    applicationPatchError,
    applicationPatchSuccess,
    applicationPatchFinished,
    applicationSetState,
    applicationSetInternalApproved,
    hireSetData,
    hireSetHireDate,
    hireSetState,
} from "./actions";

function* patchApplication({
    payload: {
        id,
        rejectReasons,
        otherReason,
        reschedule = false,
        options = {},
        isDelayed = false,
        onComplete = null,
    },
}) {
    const { application } = yield select((state) => ({
        application: state.applications[id],
    }));
    const uid = yield select((state) => state.ws.permissionUser);

    const data = {
        state: application.state,
        ...options,
    };

    const isRejected = application.state === APPLICATION_STATE.REJECTED;

    if (isRejected) {
        /* eslint-disable no-bitwise */
        data.reason = rejectReasons.reduce((memo, reason) => {
            const num = Number(reason);
            return memo | num;
        }, 0);
        if (otherReason) {
            data.reason_other = otherReason;
        }

        if (reschedule) {
            data.reschedule = true;
        }
        /* eslint-enable */
    }

    if (!isDelayed) {
        yield put(applicationPatchStart());
    }

    heapTrack("Application Patch", {
        ...data,
        id,
        auth_uid: uid,
    });

    try {
        const result = yield call(patch, `/api/hire/v1/application/${id}/`, data);

        if (!isDelayed) {
            yield put(applicationPatchSuccess(result.id));
        }
    } catch (e) {
        rollbarXHRError("Failed to patch application", e, {
            applicationID: id,
            ...data,
        });

        if (!isDelayed) {
            yield put(applicationPatchError(e));
        }
    } finally {
        if (!isDelayed) {
            yield put(applicationPatchFinished());
        }

        if (onComplete) {
            onComplete();
        }
    }
}

/**
 * Admin-only application approval.
 */
function* approveApplication({ payload }) {
    const { application, isAdmin } = yield select((state) => ({
        application: state.applications[payload],
        isAdmin: state.ws.isAdmin,
    }));

    if (!isAdmin) {
        return;
    }

    yield put(applicationPatchStart());

    try {
        yield call(patch, `/api/hire/v1/application/${application.id}/`, { approved: true });
    } catch (e) {
        rollbarXHRError("Failed to approve application", e, {
            applicationID: application.id,
        });
    } finally {
        yield put(applicationPatchFinished());
    }
}

function* patchHireDate({ payload: { id, date_employed, date_terminated } }) {
    const { hire } = yield select((state) => ({
        hire: state.hires[id],
    }));

    // eslint-disable-next-line camelcase
    if (!(date_employed || date_terminated)) {
        return;
    }

    yield put(applicationPatchStart());

    try {
        yield call(patch, `/api/hire/v1/hire/${hire.id}/`, {
            date_employed, // eslint-disable-line camelcase
            date_terminated, // eslint-disable-line camelcase
        });
    } catch (e) {
        rollbarXHRError("Failed to patch hire date", e, {
            hireID: hire.id,
            date_employed, // eslint-disable-line camelcase
            date_terminated, // eslint-disable-line camelcase
        });
    } finally {
        yield put(applicationPatchFinished());
    }
}

function* patchHire({
    payload: { id, state, options = {}, shouldHideModal = true, onSuccess = null },
}) {
    const data = {
        state,
        ...options,
    };

    yield put(uiSetSubmitting(true));

    let path = "";
    let method = post;
    if (id) {
        path = `${id}/`;
        method = patch;
    }

    try {
        const result = yield call(method, `/api/hire/v1/hire/${path}`, data);

        if (shouldHideModal) {
            yield put(hideModal());
        }

        if (onSuccess) {
            onSuccess();
        }

        if (options.application_id) {
            yield put(
                applicationSetState({
                    id: options.application_id,
                    state: APPLICATION_STATE.EMPLOYED,
                }),
            );
            yield put(hireSetData(result));
            yield put(applicationPatchSuccess(options.application_id));
        }
    } catch (e) {
        rollbarXHRError("Failed to patch hire", e, {
            id,
            method,
            ...data,
        });
        yield put(uiSetAsyncError("We failed to update this hire. Please try again."));
    } finally {
        yield put(uiSetSubmitting(false));
    }
}

export default function* applicationSagas() {
    yield all([
        takeEvery(applicationPatch, patchApplication),
        takeEvery(applicationSetInternalApproved, approveApplication),
        takeEvery(hireSetState, patchHire),
        takeEvery(hireSetHireDate, patchHireDate),
    ]);
}
